/**
 * @fileoverview Base class for the map's dropdown-menu controls (tile set switcher, region,
 * result overlays, map effects).
 * @module map/DropdownControl
 */


/**
 * Base OpenLayers control for a button that toggles a dropdown panel.
 * Provides the shared parts of every dropdown control: the button + dropdown elements, the
 * toggle-on-click, closing the dropdown on a click anywhere else in the page, and closing the
 * sibling dropdowns when this one opens.
 *
 * The "click anywhere else" listener lives on the document, so it is registered in setMap()
 * when the control is added to a map and removed when it is taken off one (OpenLayers calls
 * setMap(null) for every control when its map is disposed). A world switch builds a new map
 * with new controls, and the old controls' document listeners used to stay behind forever,
 * keeping the previous world's whole map alive.
 * @class
 * @extends ol.control.Control
 */
export class DropdownControl extends ol.control.Control {
    /**
     * Creates the control's button and (hidden) dropdown elements.
     * @param {Object} options - Control options
     * @param {string} options.buttonIconHtml - Inner HTML of the button (an icon)
     * @param {string} options.buttonClass - CSS class of the button
     * @param {string} options.buttonTitle - Tooltip of the button
     * @param {string} options.elementClass - CSS class of the control element (ol-unselectable ol-control are added)
     * @param {string} options.dropdownClass - CSS class of the dropdown panel
     * @param {HTMLElement} [options.target] - Target element to render the control into
     */
    constructor(options) {
        const button = document.createElement('button');
        button.innerHTML = options.buttonIconHtml;
        button.className = options.buttonClass;
        button.title = options.buttonTitle;

        const element = document.createElement('div');
        element.className = `${options.elementClass} ol-unselectable ol-control`;
        element.appendChild(button);

        super({
            element: element,
            target: options.target
        });

        this.siblingControls = null;

        // Create dropdown menu
        this.dropdown = document.createElement('div');
        this.dropdown.className = options.dropdownClass;
        this.dropdown.style.display = 'none';
        element.appendChild(this.dropdown);

        // Toggle dropdown on button click
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleDropdown();
        });

        // Close dropdown when clicking outside (registered on the document by setMap)
        this.documentClickHandler = (e) => {
            if (!element.contains(e.target)) {
                this.hideDropdown();
            }
        };
        this.documentClickHandlerInstalled = false;
    }

    /**
     * Adds the document-level outside-click listener while the control is on a map, and
     * removes it when the control is taken off (setMap(null)).
     * @param {ol.Map|null} map - The map, or null to remove the control from its map
     * @override
     */
    setMap(map) {
        if (this.documentClickHandlerInstalled) {
            document.removeEventListener('click', this.documentClickHandler);
            this.documentClickHandlerInstalled = false;
        }
        super.setMap(map);
        if (map) {
            document.addEventListener('click', this.documentClickHandler);
            this.documentClickHandlerInstalled = true;
        }
    }

    /**
     * Sets sibling controls whose dropdowns should close when this one opens.
     * @param {Array<Object>} siblings - Array of controls with hideDropdown() method
     */
    setSiblingControls(siblings) {
        this.siblingControls = siblings;
    }

    /**
     * Hides dropdowns on all sibling controls.
     * @private
     */
    closeSiblings() {
        if (this.siblingControls) {
            for (const sibling of this.siblingControls) {
                sibling.hideDropdown();
            }
        }
    }

    /**
     * Toggles the dropdown menu visibility.
     */
    toggleDropdown() {
        const isVisible = this.dropdown.style.display === 'block';
        this.closeSiblings();
        this.dropdown.style.display = isVisible ? 'none' : 'block';
    }

    /**
     * Hides the dropdown menu.
     */
    hideDropdown() {
        this.dropdown.style.display = 'none';
    }
}
